import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useState } from "react";

import { EncounterChangeConditionFormValues } from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { getSubmissionFormStyles } from "@modules/booking/screens/booking-calendar/components/appointment-dialog/components/appointment-form/AppointmentForm.styles.tsx";
import { SecondColumnContent } from "@shared-types/booking/second-column-content.enum.ts";
import { ClinicalRecord } from "@stores/clinical/models/ClinicalRecord.ts";
import { Encounter } from "@stores/clinical/models/Encounter.ts";
import { EpisodeOfCare } from "@stores/clinical/models/EpisodeOfCare.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { DataFetcher } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { SubmissionFormDialog } from "@ui-components/form/submission-form-dialog/SubmissionFormDialog.tsx";

import { ChangeConditionModalFields } from "./ChangeConditionModalFields.tsx";
import { ChangeConditionSecondColumn } from "./ChangeConditionSecondColumn.tsx";
import { ChangeConditionContext } from "./context/ChangeConditionContext.ts";

interface PastVisitChangeConditionProps {
  clinicalRecord: ClinicalRecord;
  encounter: Encounter;
}

export const PastVisitChangeCondition: React.FunctionComponent<PastVisitChangeConditionProps> =
  observer(({ clinicalRecord, encounter }) => {
    const { clinical } = useStores();
    const { setShowChangeConditionDialog, showChangeConditionSecondColumn } =
      useContext(ChangeConditionContext);

    const [currentEpisodeOfCare, setCurrentEpisodeOfCare] = useState<
      EpisodeOfCare | undefined
    >(clinicalRecord.episodeOfCare ?? undefined);

    useEffect(() => {
      const fetchEpisodeOfCare = async () => {
        if (encounter.episodeOfCareId) {
          const fetchedEpisodeOfCare = await clinical.getEpisodeOfCare(
            encounter.episodeOfCareId
          );
          setCurrentEpisodeOfCare(fetchedEpisodeOfCare);
        }
      };

      fetchEpisodeOfCare();
    }, [encounter.episodeOfCareId, clinical]);

    const getEpisodesOfCares = async () => {
      const patientId = clinicalRecord?.patient?.id ?? "";

      if (!patientId) {
        return;
      }

      return await clinical.getPatientEpisodesOfCare(patientId);
    };

    return (
      <DataFetcher
        fetch={getEpisodesOfCares}
        refetchId={clinicalRecord?.patient?.id}
      >
        {episodeOfCares => (
          <SubmissionFormDialog<EncounterChangeConditionFormValues>
            dialogName="changeCondition"
            hideButtons
            dialogProps={{
              maxWidth: "auto",
              minWidth: "auto",
              onDismiss: () => {
                setShowChangeConditionDialog(false);
              },
              dialogContentProps: {
                styles: {
                  title: { display: "none" },
                  inner: { padding: 0, height: "100%" },
                  innerContent: { padding: 0 }
                }
              }
            }}
            onSubmit={() => {}}
            buttonsProps={{
              submitButtonProps: {
                text: "Continue"
              },
              cancelButtonProps: {
                text: "Cancel"
              },
              onCancel: () => {
                setShowChangeConditionDialog(false);
              },
              hideButtonsSeparator: true
            }}
            styles={getSubmissionFormStyles(
              showChangeConditionSecondColumn
                ? SecondColumnContent.conditionDetails
                : undefined
            )}
            sidePanel={
              <ChangeConditionSecondColumn
                episodeOfCares={episodeOfCares}
                patientId={clinicalRecord?.patient?.id}
                setCurrentEpisodeOfCare={setCurrentEpisodeOfCare}
                currentEpisodeOfCare={currentEpisodeOfCare}
                encounter={encounter}
              />
            }
          >
            {() => (
              <ChangeConditionModalFields
                currentEpisodeOfCare={currentEpisodeOfCare}
                clinicalRecord={clinicalRecord}
                encounter={encounter}
              />
            )}
          </SubmissionFormDialog>
        )}
      </DataFetcher>
    );
  });
